import { ACTIONS_CONFIG_PAYLOAD_ENUM, ACTIONS_ENUM } from 'types/enums.ts';
import type { Action, ActionsConfig } from 'types';
import { createRandomString } from 'utils/common';
import { toLowerCamelCase } from 'utils/string';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  isCreateAction,
  isChangeAction,
  isDeleteNotificationAction,
  isNotifyAction,
  isRedirectAction,
  isDistinctForeachAction,
  isCreateForEachAction,
  isSendDataToErpAction,
  isTakeDataFromErpAction,
  isExternalAction,
  isExternalRestApiAction,
  isGoToExternalCatalogueAction,
  isPurchaseExternalCatalogueAction,
  isSendDataToErpWithItemsAction,
  safeMapArray,
  isDialogAction,
} from './actionTypeGuards';

const EMPTY_ACTION = {
  necessaryStepsNumber: 0,
  necessarySteps: [],
  isCompleted: false,
  type: ACTIONS_ENUM.Empty,
};

export const actionsConfig: {
  [key in ACTIONS_ENUM]: ActionsConfig;
} = {
  [ACTIONS_ENUM.Archive]: {
    necessaryStepsNumber: 1,
    necessarySteps: [],
    isCompleted: false,
    type: ACTIONS_ENUM.Archive,
  },
  [ACTIONS_ENUM.Delete]: {
    necessaryStepsNumber: 1,
    necessarySteps: [],
    isCompleted: false,
    type: ACTIONS_ENUM.Delete,
  },
  [ACTIONS_ENUM.IncreaseVersion]: {
    necessaryStepsNumber: 1,
    necessarySteps: [],
    isCompleted: false,
    type: ACTIONS_ENUM.IncreaseVersion,
  },
  [ACTIONS_ENUM.Notify]: {
    necessaryStepsNumber: 2,
    necessarySteps: ['Select Notification'],
    isCompleted: false,
    type: ACTIONS_ENUM.Notify,
  },
  [ACTIONS_ENUM.DeleteNotification]: {
    necessaryStepsNumber: 1,
    necessarySteps: ['Select Notification'],
    isCompleted: false,
    type: ACTIONS_ENUM.DeleteNotification,
  },
  [ACTIONS_ENUM.Change]: {
    necessaryStepsNumber: 1,
    necessarySteps: ['Select Field'],
    isCompleted: false,
    type: ACTIONS_ENUM.Change,
  },
  [ACTIONS_ENUM.Create]: {
    necessaryStepsNumber: 2,
    necessarySteps: ['Select Field'],
    isCompleted: false,
    type: ACTIONS_ENUM.Create,
  },
  [ACTIONS_ENUM.DistinctForeach]: {
    necessaryStepsNumber: 3,
    necessarySteps: ['Select Field'],
    isCompleted: false,
    type: ACTIONS_ENUM.DistinctForeach,
  },
  [ACTIONS_ENUM.CreateForEach]: {
    necessaryStepsNumber: 3,
    necessarySteps: ['Select Field'],
    isCompleted: false,
    type: ACTIONS_ENUM.CreateForEach,
  },
  [ACTIONS_ENUM.Redirect]: {
    necessaryStepsNumber: 1,
    necessarySteps: ['Redirect'],
    isCompleted: false,
    type: ACTIONS_ENUM.Redirect,
  },
  [ACTIONS_ENUM.SendDataToErp]: {
    necessaryStepsNumber: 2,
    necessarySteps: ['Send Data To ERP'],
    isCompleted: false,
    type: ACTIONS_ENUM.SendDataToErp,
  },
  [ACTIONS_ENUM.TakeDataFromErp]: {
    necessaryStepsNumber: 2,
    necessarySteps: ['Take Data From ERP'],
    isCompleted: false,
    type: ACTIONS_ENUM.TakeDataFromErp,
  },
  [ACTIONS_ENUM.ExternalAction]: {
    necessaryStepsNumber: 3,
    necessarySteps: ['External Action'],
    isCompleted: false,
    type: ACTIONS_ENUM.ExternalAction,
  },
  [ACTIONS_ENUM.ExternalRestApi]: {
    necessaryStepsNumber: 1,
    necessarySteps: ['External Rest Api Action'],
    isCompleted: false,
    type: ACTIONS_ENUM.ExternalRestApi,
  },
  [ACTIONS_ENUM.GoToExternalCatalogue]: {
    necessaryStepsNumber: 3,
    necessarySteps: ['Go To External Catalogue'],
    isCompleted: false,
    type: ACTIONS_ENUM.GoToExternalCatalogue,
  },
  [ACTIONS_ENUM.PurchaseExternalCatalogue]: {
    necessaryStepsNumber: 3,
    necessarySteps: ['Purchase External Catalogue'],
    isCompleted: false,
    type: ACTIONS_ENUM.PurchaseExternalCatalogue,
  },
  [ACTIONS_ENUM.SendDataToErpWithItems]: {
    necessaryStepsNumber: 2,
    necessarySteps: ['Send Data To ERP With Items'],
    isCompleted: false,
    type: ACTIONS_ENUM.SendDataToErpWithItems,
  },
  [ACTIONS_ENUM.Dialog]: {
    necessaryStepsNumber: 1,
    necessarySteps: ['Dialog'],
    isCompleted: false,
    type: ACTIONS_ENUM.Dialog,
  },
  [ACTIONS_ENUM.Halt]: {
    necessaryStepsNumber: 1,
    necessarySteps: ['Halt'],
    isCompleted: false,
    type: ACTIONS_ENUM.Halt,
  },
  [ACTIONS_ENUM.Empty]: EMPTY_ACTION,
};

interface UseActionsProps {
  actions?: Action[];
  isLoading: boolean;
}

type UseActionsReturnType = {
  actions: {
    [key: string]: ActionsConfig;
  };
  actionOrder: string[];
  onAddAction: (action: ACTIONS_ENUM, afterId?: string) => void;
  onUpdateActionType: (id: string, action: ACTIONS_ENUM) => void;
  onDeleteAction: (id: string) => void;
  onCompleteSteps: (id: string) => void;
  onUpdateAction: (id: string, action: ActionsConfig) => void;
  onReorderActions: (actionIds: string[]) => void;
  onCreatePayloadForBE: () => Action[];
  isActionsSetup: boolean;
};

export const useActions = ({ actions: existingActions, isLoading }: UseActionsProps): UseActionsReturnType => {
  const { t } = useTranslation();
  const [actions, setActions] = useState<{
    [key: string]: ActionsConfig;
  }>({});
  const [actionOrder, setActionOrder] = useState<string[]>([]);

  const [isActionsSetup, setIsActionsSetup] = useState(false);

  useEffect(() => {
    if (Object.keys(actions).length != 0) return;

    setIsActionsSetup(false);

    if (isLoading) return;

    if (!existingActions || existingActions.length === 0) {
      const initialActionId = createRandomString(10);
      const initialActions = {
        [initialActionId]: actionsConfig[ACTIONS_ENUM.Empty],
      };

      setActions(initialActions);
      setActionOrder([initialActionId]);
      setIsActionsSetup(true);
      return;
    }

    const newActions: {
      [key: string]: ActionsConfig;
    } = {};
    const newActionOrder: string[] = [];

    const getExistingActionValuesByType = (type: ACTIONS_ENUM, action: Action) => {
      try {
        switch (type) {
          case ACTIONS_ENUM.Create: {
            if (!isCreateAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.Create]: {
                id: action.id,
                otherRecordFormItemId: action.createAction.otherRecordFormItemId ?? undefined,
                otherRecordAgendaId: action.createAction.otherRecordAgendaId ?? undefined,
                otherRecordModuleId: action.createAction.otherRecordModuleId ?? undefined,
                otherRecordViewId: action.createAction.otherRecordViewId ?? undefined,
                viewFilterParams: action.createAction.viewFilterParams ?? [],
                values: safeMapArray(action.createAction.values, (value) => ({
                  id: value.id,
                  targetFormItemId: value.targetFormItemId,
                  changeStrategy: value.changeStrategy,
                  valueDirect: value.valueDirect,
                  valueSourceType: value.valueSourceType,
                  valueFormItemId: value.valueFormItemId,
                  valueFormItemName: value.valueFormItemName,
                  userValueMatchColumn: value.userValueMatchColumn,
                  valueMatchColumn: value.valueMatchColumn,
                  valueMatchUserDataColumn: value.valueMatchUserDataColumn,
                })),
                executeActionsForCreatedRecord: action.createAction.executeActionsForCreatedRecord ?? false,
              },
            };
          }

          case ACTIONS_ENUM.Change: {
            if (!isChangeAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.Change]: {
                otherRecordFormItemId: action.changeAction.otherRecordFormItemId ?? undefined,
                otherRecordAgendaId: action.changeAction.otherRecordAgendaId ?? undefined,
                otherRecordModuleId: action.changeAction.otherRecordModuleId ?? undefined,
                otherRecordViewId: action.changeAction.otherRecordViewId ?? undefined,
                viewFilterParams: action.changeAction.viewFilterParams ?? [],
                values: safeMapArray(action.changeAction.values, (value) => ({
                  targetFormItemId: value.targetFormItemId,
                  changeStrategy: value.changeStrategy,
                  valueDirect: value.valueDirect,
                  valueSourceType: value.valueSourceType,
                  valueFormItemId: value.valueFormItemId,
                  valueFormItemName: value.valueFormItemName ?? '',
                  userValueMatchColumn: value.userValueMatchColumn,
                  valueMatchColumn: value.valueMatchColumn,
                  valueMatchUserDataColumn: value.valueMatchUserDataColumn,
                  valueFormItemMatchColumnId: value.valueFormItemMatchColumnId,
                })),
                executeActionsForChangedRecord: action.changeAction.executeActionsForChangedRecord ?? false,
              },
            };
          }

          case ACTIONS_ENUM.DeleteNotification: {
            if (!isDeleteNotificationAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.DeleteNotification]: {
                id: action.id,
                sectionToDeleteId: action.deleteNotificationAction.sectionToDeleteId ?? undefined,
                otherRecordFormItemId: action.deleteNotificationAction.otherRecordFormItemId ?? undefined,
                otherRecordAgendaId: action.deleteNotificationAction.otherRecordAgendaId ?? undefined,
                otherRecordViewId: action.deleteNotificationAction.otherRecordViewId ?? undefined,
                viewFilterParams: action.deleteNotificationAction.viewFilterParams ?? [],
              },
            };
          }

          case ACTIONS_ENUM.Notify: {
            if (!isNotifyAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.Notify]: {
                id: action.id,
                notificationTemplateId: action.notifyAction.notificationTemplateId ?? undefined,
                requiresAction: action.notifyAction.requiresAction ?? false,
                state: action.notifyAction.state ?? undefined,
                otherRecordFormItemId: action.notifyAction.otherRecordFormItemId ?? undefined,
                otherRecordAgendaId: action.notifyAction.otherRecordAgendaId ?? undefined,
                otherRecordViewId: action.notifyAction.otherRecordViewId ?? undefined,
                viewFilterParams: action.notifyAction.viewFilterParams ?? [],
                values: safeMapArray(action.notifyAction.values, (value) => ({
                  parameterId: value.parameterId,
                  targetFormItemId: value.targetFormItemId,
                  valueDirect: value.valueDirect,
                  valueSourceType: value.valueSourceType,
                  valueFormItemId: value.valueFormItemId,
                  userValueMatchColumn: value.userValueMatchColumn,
                  valueMatchColumn: value.valueMatchColumn,
                })),
                addressees: action.notifyAction.addressees ?? [],
              },
            };
          }

          case ACTIONS_ENUM.Redirect: {
            if (!isRedirectAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.Redirect]: {
                id: action.id,
                otherRecordFormItemId: action.redirectAction.otherRecordFormItemId ?? undefined,
                otherAgendaId: action.redirectAction.otherAgendaId ?? undefined,
                otherViewId: action.redirectAction.otherViewId ?? undefined,
                redirectToLastCreatedRecord: action.redirectAction.redirectToLastCreatedRecord ?? undefined,
              },
            };
          }

          case ACTIONS_ENUM.DistinctForeach: {
            if (!isDistinctForeachAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.DistinctForeach]: {
                otherRecordAgendaId: action.distinctForeachAction.otherRecordAgendaId ?? undefined,
                otherRecordModuleId: action.distinctForeachAction.otherRecordModuleId ?? undefined,
                otherRecordViewId: action.distinctForeachAction.otherRecordViewId ?? undefined,
                distinctFormItemId: action.distinctForeachAction.distinctFormItemId ?? undefined,
                values: safeMapArray(action.distinctForeachAction.values, (value) => ({
                  targetFormItemId: value.targetFormItemId,
                  changeStrategy: value.changeStrategy,
                  valueDirect: value.valueDirect,
                  valueSourceType: value.valueSourceType,
                  valueFormItemId: value.valueFormItemId,
                  valueFormItemName: value.valueFormItemName,
                })),
              },
            };
          }

          case ACTIONS_ENUM.CreateForEach: {
            if (!isCreateForEachAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.CreateForEach]: {
                id: action.id,
                otherRecordAgendaId: action.createForEachAction.otherRecordAgendaId ?? undefined,
                otherRecordFormItemId: action.createForEachAction.otherRecordFormItemId ?? undefined,
                forEachFormItemId: action.createForEachAction.forEachFormItemId ?? undefined,
                values: safeMapArray(action.createForEachAction.values, (value) => ({
                  id: value.id,
                  targetFormItemId: value.targetFormItemId,
                  changeStrategy: value.changeStrategy,
                  valueDirect: value.valueDirect,
                  valueSourceType: value.valueSourceType,
                  valueFormItemId: value.valueFormItemId,
                  valueFormItemName: value.valueFormItemName,
                  userValueMatchColumn: value.userValueMatchColumn,
                  valueMatchColumn: value.valueMatchColumn,
                })),
              },
            };
          }

          case ACTIONS_ENUM.SendDataToErp: {
            if (!isSendDataToErpAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.SendDataToErp]: {
                templateId: action.sendDataToErpAction.templateId ?? undefined,
                propertyPairing: safeMapArray(action.sendDataToErpAction.propertyPairing, (value) => ({
                  formItemId: value.formItemId,
                  columnKey: value.columnKey,
                  name: value.name,
                  isKey: value.isKey,
                  arrayValuesFormItemId: value.arrayValuesFormItemId || undefined,
                })),
              },
            };
          }

          case ACTIONS_ENUM.TakeDataFromErp: {
            if (!isTakeDataFromErpAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.TakeDataFromErp]: {
                templateId: action.takeDataFromErpAction.templateId ?? undefined,
                targetModuleId: action.takeDataFromErpAction.targetModuleId ?? undefined,
                targetAgendaId: action.takeDataFromErpAction.targetAgendaId ?? undefined,
                filter: safeMapArray(action.takeDataFromErpAction.filter, (value) => ({
                  formItemId: value.formItemId,
                  columnKey: value.columnKey,
                  name: value.name,
                  valueDirect: value.valueDirect,
                })),
                propertyPairing: safeMapArray(action.takeDataFromErpAction.propertyPairing, (value) => ({
                  formItemId: value.formItemId,
                  columnKey: value.columnKey,
                  name: value.name,
                  isKey: value.isKey,
                })),
              },
            };
          }

          case ACTIONS_ENUM.ExternalAction: {
            if (!isExternalAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.ExternalAction]: {
                templateId: action.externalAction.templateId ?? undefined,
                functionId: action.externalAction.functionId ?? undefined,
                inputParameters: safeMapArray(action.externalAction.inputParameters, (value) => ({
                  formItemId: value.formItemId,
                  name: value.name,
                })),
                outputParameters: safeMapArray(action.externalAction.outputParameters, (value) => ({
                  formItemId: value.formItemId,
                  columnKey: value.columnKey,
                })),
              },
            };
          }

          case ACTIONS_ENUM.ExternalRestApi: {
            if (!isExternalRestApiAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.ExternalRestApi]: {
                restApiTemplateId: action.externalRestApi.restApiTemplateId,
                restApiConnectionId: action.externalRestApi.restApiConnectionId,
                apiLogLevel: action.externalRestApi.apiLogLevel,
                arrayJsonPath: action.externalRestApi.arrayJsonPath ?? null,
                otherRecordAgendaId: action.externalRestApi.otherRecordAgendaId,
                otherRecordFormItemId: action.externalRestApi.otherRecordFormItemId,
                otherRecordViewId: action.externalRestApi.otherRecordViewId,
                headers: action.externalRestApi.headers,
                inputValues: action.externalRestApi.inputValues,
                outputValues: action.externalRestApi.outputValues.map((value) => ({
                  ...value,
                  isKey: value.isKey ?? false,
                })),
              },
            };
          }

          case ACTIONS_ENUM.GoToExternalCatalogue: {
            if (!isGoToExternalCatalogueAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.GoToExternalCatalogue]: {
                initialPairing: action.goToExternalCatalogueAction.initialPairing,
                responseHeader: action.goToExternalCatalogueAction.responseHeader,
                responseItems: action.goToExternalCatalogueAction.responseItems,
                additionalPairing: action.goToExternalCatalogueAction.additionalPairing,
              },
            };
          }

          case ACTIONS_ENUM.PurchaseExternalCatalogue: {
            if (!isPurchaseExternalCatalogueAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }
            return {
              [ACTIONS_ENUM.PurchaseExternalCatalogue]: {
                recordsFormItemId: action.purchaseExternalCatalogueAction.recordsFormItemId ?? undefined,
                sendHeader: action.purchaseExternalCatalogueAction.sendHeader,
                sendItems: action.purchaseExternalCatalogueAction.sendItems,
                receiveHeader: action.purchaseExternalCatalogueAction.receiveHeader,
                receiveItems: action.purchaseExternalCatalogueAction.receiveItems,
              },
            };
          }

          case ACTIONS_ENUM.SendDataToErpWithItems: {
            if (!isSendDataToErpWithItemsAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }

            return {
              [ACTIONS_ENUM.SendDataToErpWithItems]: {
                templateId: action.sendDataToErpWithItemsAction.templateId ?? undefined,
                propertyPairing: safeMapArray(action.sendDataToErpWithItemsAction.propertyPairing, (value) => ({
                  formItemId: value.formItemId,
                  columnKey: value.columnKey,
                  name: value.name,
                  isKey: value.isKey,
                  arrayValuesFormItemId: value.arrayValuesFormItemId || undefined,
                })),
                itemsFormItemId: action.sendDataToErpWithItemsAction.itemsFormItemId ?? undefined,
                itemsTemplateId: action.sendDataToErpWithItemsAction.itemsTemplateId ?? undefined,
                itemsPropertyPairing: safeMapArray(action.sendDataToErpWithItemsAction.itemsPropertyPairing, (value) => ({
                  formItemId: value.formItemId,
                  columnKey: value.columnKey,
                  name: value.name,
                  isKey: value.isKey,
                  arrayValuesFormItemId: value.arrayValuesFormItemId || undefined,
                })),
              },
            };
          }

          case ACTIONS_ENUM.Dialog: {
            if (!isDialogAction(action, type)) {
              toast.warning(t('toast.action.loadFailed', { actionType: type }));
              return {};
            }

            return {
              [ACTIONS_ENUM.Dialog]: {
                notificationTemplateId: action.dialogAction.notificationTemplateId,
                icon: action.dialogAction.icon,
                titleInput: action.dialogAction.titleInput,
                values: action.dialogAction.values,
                actionableFormItems: action.dialogAction.actionableFormItems,
              },
            };
          }

          case ACTIONS_ENUM.IncreaseVersion:
          case ACTIONS_ENUM.Archive:
          case ACTIONS_ENUM.Halt:
          case ACTIONS_ENUM.Delete:
            return {};

          default:
            return {};
        }
      } catch (_error) {
        toast.error(t('toast.action.loadFailed', { actionType: type }));
        return {};
      }
    };

    existingActions.forEach((action) => {
      if (!action) return;

      newActionOrder.push(action.id);
      newActions[action.id] = {
        ...actionsConfig[toLowerCamelCase(action.actionType) as ACTIONS_ENUM],
        type: toLowerCamelCase(action.actionType) as ACTIONS_ENUM,
        isCompleted: true,
        ...(getExistingActionValuesByType(toLowerCamelCase(action.actionType) as ACTIONS_ENUM, action) as ActionsConfig[
          | ACTIONS_ENUM.Change
          | ACTIONS_ENUM.Create
          | ACTIONS_ENUM.DeleteNotification
          | ACTIONS_ENUM.Notify
          | ACTIONS_ENUM.DistinctForeach
          | ACTIONS_ENUM.CreateForEach]),
      };
    });

    setActions(newActions);
    setActionOrder(newActionOrder);
    setIsActionsSetup(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingActions, isLoading]);

  const handleAddNewAction = (action: ACTIONS_ENUM, afterId?: string) => {
    const newActionId = createRandomString(10);
    const newAction = {
      [newActionId]: actionsConfig[action],
    };

    setActions((prev) => {
      const nextActions = {
        ...prev,
        ...newAction,
      };
      return nextActions;
    });
    setActionOrder((prev) => {
      const nextOrder = [...prev];
      const insertAt = afterId ? nextOrder.findIndex((actionId) => actionId === afterId) : -1;

      // Keep the new row near the button that created it so the flow reads top-to-bottom.
      if (insertAt >= 0) {
        nextOrder.splice(insertAt + 1, 0, newActionId);
      } else {
        nextOrder.push(newActionId);
      }

      return nextOrder;
    });
  };

  const handleUpdateActionType = (id: string, action: ACTIONS_ENUM) => {
    const newAction = {
      [id]: actionsConfig[action],
    };

    setActions((prev) => {
      const nextActions = {
        ...prev,
        ...newAction,
      };
      return nextActions;
    });
  };

  const handleRemoveAction = (id: string) => {
    setActions((prev) => {
      const nextActions = { ...prev };
      delete nextActions[id];
      return nextActions;
    });
    setActionOrder((prev) => {
      const nextOrder = prev.filter((actionId) => actionId !== id);
      return nextOrder;
    });
  };

  const handleCompleteSteps = (id: string) => {
    setActions((prev) => {
      const action = prev[id];
      if (!action || action.isCompleted) {
        return prev;
      }

      return {
        ...prev,
        [id]: {
          ...action,
          isCompleted: true,
        },
      };
    });
  };

  const handleUpdateAction = (id: string, action: ActionsConfig) => {
    const newActions = {
      [id]: action,
    };

    setActions((prev) => {
      const nextActions = {
        ...prev,
        ...newActions,
      };
      return nextActions;
    });
  };

  const handleReorderActions = (actionIds: string[]) => {
    setActionOrder(actionIds);
  };

  const createPayloadForBE = useCallback((): Action[] => {
    const payload = actionOrder
      .map((actionId) => actions[actionId])
      .filter((action): action is ActionsConfig => {
        if (!action) {
          return false;
        }

        return action.type !== ACTIONS_ENUM.Empty;
      })
      .map((action) => {
        switch (action.type) {
          case ACTIONS_ENUM.Create:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                id: action[ACTIONS_ENUM.Create]?.id || undefined,
                otherRecordFormItemId: action[ACTIONS_ENUM.Create]?.otherRecordFormItemId || undefined,
                otherRecordAgendaId: action[ACTIONS_ENUM.Create]?.otherRecordAgendaId || undefined,
                otherRecordModuleId: action[ACTIONS_ENUM.Create]?.otherRecordModuleId || undefined,
                otherRecordViewId: action[ACTIONS_ENUM.Create]?.otherRecordViewId || undefined,
                viewFilterParams: action[ACTIONS_ENUM.Create]?.viewFilterParams || [],
                values:
                  action[ACTIONS_ENUM.Create]?.values.map((value) => ({
                    id: value.id || undefined,
                    targetFormItemId: value.targetFormItemId,
                    valueDirect: value.valueDirect,
                    valueSourceType: value.valueSourceType,
                    valueFormItemId: value.valueFormItemId,
                    userValueMatchColumn: value.userValueMatchColumn,
                    valueMatchColumn: value.valueMatchColumn,
                    valueMatchUserDataColumn: value.valueMatchUserDataColumn,
                    changeStrategy: value.changeStrategy,
                  })) ?? [],
                executeActionsForCreatedRecord: action[ACTIONS_ENUM.Create]?.executeActionsForCreatedRecord ?? false,
              },
            };

          case ACTIONS_ENUM.Change:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                otherRecordFormItemId: action[ACTIONS_ENUM.Change]?.otherRecordFormItemId || undefined,
                otherRecordAgendaId: action[ACTIONS_ENUM.Change]?.otherRecordAgendaId || undefined,
                otherRecordViewId: action[ACTIONS_ENUM.Change]?.otherRecordViewId || undefined,
                viewFilterParams: action[ACTIONS_ENUM.Change]?.viewFilterParams || [],
                values:
                  action[ACTIONS_ENUM.Change]?.values.map((value) => ({
                    targetFormItemId: value.targetFormItemId,
                    valueDirect: value.valueDirect,
                    valueSourceType: value.valueSourceType,
                    valueFormItemId: value.valueFormItemId,
                    userValueMatchColumn: value.userValueMatchColumn,
                    valueMatchColumn: value.valueMatchColumn,
                    valueMatchUserDataColumn: value.valueMatchUserDataColumn,
                    valueFormItemMatchColumnId: value.valueFormItemMatchColumnId,
                    changeStrategy: value.changeStrategy,
                  })) ?? [],
                executeActionsForChangedRecord: action[ACTIONS_ENUM.Change]?.executeActionsForChangedRecord ?? false,
              },
            };

          case ACTIONS_ENUM.DeleteNotification:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                sectionToDeleteId: action[ACTIONS_ENUM.DeleteNotification]?.sectionToDeleteId || undefined,
                otherRecordFormItemId: action[ACTIONS_ENUM.DeleteNotification]?.otherRecordFormItemId || undefined,
                otherRecordAgendaId: action[ACTIONS_ENUM.DeleteNotification]?.otherRecordAgendaId || undefined,
                otherRecordViewId: action[ACTIONS_ENUM.DeleteNotification]?.otherRecordViewId || undefined,
                viewFilterParams: action[ACTIONS_ENUM.DeleteNotification]?.viewFilterParams || [],
              },
            };

          case ACTIONS_ENUM.Notify:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                id: action[ACTIONS_ENUM.Notify]?.id || undefined,
                notificationTemplateId: action[ACTIONS_ENUM.Notify]?.notificationTemplateId || undefined,
                requiresAction: action[ACTIONS_ENUM.Notify]?.requiresAction || false,
                state: action[ACTIONS_ENUM.Notify]?.state || undefined,
                otherRecordFormItemId: action[ACTIONS_ENUM.Notify]?.otherRecordFormItemId || undefined,
                otherRecordAgendaId: action[ACTIONS_ENUM.Notify]?.otherRecordAgendaId || undefined,
                otherRecordViewId: action[ACTIONS_ENUM.Notify]?.otherRecordViewId || undefined,
                viewFilterParams: action[ACTIONS_ENUM.Notify]?.viewFilterParams || [],
                values:
                  action[ACTIONS_ENUM.Notify]?.values?.map((value) => ({
                    parameterId: value.parameterId || undefined,
                    targetFormItemId: value.targetFormItemId,
                    valueDirect: value.valueDirect,
                    valueSourceType: value.valueSourceType,
                    valueFormItemId: value.valueFormItemId,
                    userValueMatchColumn: value.userValueMatchColumn,
                    valueMatchColumn: value.valueMatchColumn,
                  })) ?? [],
                addressees: action[ACTIONS_ENUM.Notify]?.addressees || [],
              },
            };

          case ACTIONS_ENUM.DistinctForeach:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                otherRecordAgendaId: action[ACTIONS_ENUM.DistinctForeach]?.otherRecordAgendaId || undefined,
                otherRecordViewId: action[ACTIONS_ENUM.DistinctForeach]?.otherRecordViewId || undefined,
                values:
                  action[ACTIONS_ENUM.DistinctForeach]?.values?.map((value) => ({
                    targetFormItemId: value.targetFormItemId,
                    valueDirect: value.valueDirect,
                    valueSourceType: value.valueSourceType,
                    valueFormItemId: value.valueFormItemId,
                    changeStrategy: value.changeStrategy,
                  })) ?? [],
                distinctFormItemId: action[ACTIONS_ENUM.DistinctForeach]?.distinctFormItemId || undefined,
              },
            };

          case ACTIONS_ENUM.CreateForEach:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                id: action[ACTIONS_ENUM.CreateForEach]?.id || undefined,
                otherRecordFormItemId: action[ACTIONS_ENUM.CreateForEach]?.otherRecordFormItemId || undefined,
                otherRecordAgendaId: action[ACTIONS_ENUM.CreateForEach]?.otherRecordAgendaId || undefined,
                forEachFormItemId: action[ACTIONS_ENUM.CreateForEach]?.forEachFormItemId || undefined,
                values:
                  action[ACTIONS_ENUM.CreateForEach]?.values?.map((value) => ({
                    id: value.id || undefined,
                    targetFormItemId: value.targetFormItemId,
                    valueDirect: value.valueDirect,
                    valueSourceType: value.valueSourceType,
                    valueFormItemId: value.valueFormItemId,
                    userValueMatchColumn: value.userValueMatchColumn,
                    valueMatchColumn: value.valueMatchColumn,
                    changeStrategy: value.changeStrategy,
                  })) ?? [],
              },
            };

          case ACTIONS_ENUM.Redirect:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                otherRecordFormItemId: action[ACTIONS_ENUM.Redirect]?.otherRecordFormItemId || undefined,
                otherAgendaId: action[ACTIONS_ENUM.Redirect]?.otherAgendaId || undefined,
                otherViewId: action[ACTIONS_ENUM.Redirect]?.otherViewId || undefined,
                redirectToLastCreatedRecord: action[ACTIONS_ENUM.Redirect]?.redirectToLastCreatedRecord || false,
              },
            };

          case ACTIONS_ENUM.SendDataToErp:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                templateId: action[ACTIONS_ENUM.SendDataToErp]?.templateId || undefined,
                propertyPairing:
                  action[ACTIONS_ENUM.SendDataToErp]?.propertyPairing?.map((value) => ({
                    formItemId: value.formItemId,
                    columnKey: value.columnKey,
                    name: value.name,
                    isKey: value.isKey,
                    arrayValuesFormItemId: value.arrayValuesFormItemId || undefined,
                  })) || undefined,
              },
            };
          case ACTIONS_ENUM.TakeDataFromErp:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                templateId: action[ACTIONS_ENUM.TakeDataFromErp]?.templateId || undefined,
                targetModuleId: action[ACTIONS_ENUM.TakeDataFromErp]?.targetModuleId || undefined,
                targetAgendaId: action[ACTIONS_ENUM.TakeDataFromErp]?.targetAgendaId || undefined,
                filter: action[ACTIONS_ENUM.TakeDataFromErp]?.filter || [],
                propertyPairing: action[ACTIONS_ENUM.TakeDataFromErp]?.propertyPairing || undefined,
              },
            };
          case ACTIONS_ENUM.ExternalAction:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                templateId: action[ACTIONS_ENUM.ExternalAction]?.templateId || undefined,
                functionId: action[ACTIONS_ENUM.ExternalAction]?.functionId || undefined,
                inputParameters: action[ACTIONS_ENUM.ExternalAction]?.inputParameters || undefined,
                outputParameters: action[ACTIONS_ENUM.ExternalAction]?.outputParameters || undefined,
              },
            };

          case ACTIONS_ENUM.GoToExternalCatalogue:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                initialPairing: action[ACTIONS_ENUM.GoToExternalCatalogue]?.initialPairing || undefined,
                responseHeader: action[ACTIONS_ENUM.GoToExternalCatalogue]?.responseHeader || undefined,
                responseItems: action[ACTIONS_ENUM.GoToExternalCatalogue]?.responseItems || undefined,
                additionalPairing: action[ACTIONS_ENUM.GoToExternalCatalogue]?.additionalPairing || undefined,
              },
            };

          case ACTIONS_ENUM.PurchaseExternalCatalogue:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                recordsFormItemId: action[ACTIONS_ENUM.PurchaseExternalCatalogue]?.recordsFormItemId || undefined,
                sendHeader: action[ACTIONS_ENUM.PurchaseExternalCatalogue]?.sendHeader || undefined,
                sendItems: action[ACTIONS_ENUM.PurchaseExternalCatalogue]?.sendItems || undefined,
                receiveHeader: action[ACTIONS_ENUM.PurchaseExternalCatalogue]?.receiveHeader || undefined,
                receiveItems: action[ACTIONS_ENUM.PurchaseExternalCatalogue]?.receiveItems || undefined,
              },
            };

          case ACTIONS_ENUM.SendDataToErpWithItems:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                templateId: action[ACTIONS_ENUM.SendDataToErpWithItems]?.templateId || undefined,
                propertyPairing:
                  action[ACTIONS_ENUM.SendDataToErpWithItems]?.propertyPairing?.map((value) => ({
                    formItemId: value.formItemId,
                    columnKey: value.columnKey,
                    name: value.name,
                    isKey: value.isKey,
                    arrayValuesFormItemId: value.arrayValuesFormItemId || undefined,
                  })) || [],
                itemsFormItemId: action[ACTIONS_ENUM.SendDataToErpWithItems]?.itemsFormItemId || undefined,
                itemsTemplateId: action[ACTIONS_ENUM.SendDataToErpWithItems]?.itemsTemplateId || undefined,
                itemsPropertyPairing:
                  action[ACTIONS_ENUM.SendDataToErpWithItems]?.itemsPropertyPairing?.map((value) => ({
                    formItemId: value.formItemId,
                    columnKey: value.columnKey,
                    name: value.name,
                    isKey: value.isKey,
                    arrayValuesFormItemId: value.arrayValuesFormItemId || undefined,
                  })) || [],
              },
            };

          case ACTIONS_ENUM.Delete:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {},
            };

          case ACTIONS_ENUM.IncreaseVersion:
          case ACTIONS_ENUM.Archive:
          case ACTIONS_ENUM.Halt:
            return {
              id: action?.id,
              actionType: action.type,
            };
          case ACTIONS_ENUM.ExternalRestApi:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                ...action[ACTIONS_ENUM.ExternalRestApi],
                arrayJsonPath: action[ACTIONS_ENUM.ExternalRestApi]?.arrayJsonPath ?? null,
                otherRecordFormItemId: action[ACTIONS_ENUM.ExternalRestApi]?.otherRecordFormItemId ?? null,
                otherRecordAgendaId: action[ACTIONS_ENUM.ExternalRestApi]?.otherRecordAgendaId ?? null,
                otherViewId: action[ACTIONS_ENUM.ExternalRestApi]?.otherRecordViewId ?? null,
                outputValues: (action[ACTIONS_ENUM.ExternalRestApi]?.outputValues ?? []).map((value) => ({
                  ...value,
                  isKey: value.isKey ?? false,
                })),
              },
            };
          case ACTIONS_ENUM.Dialog:
            return {
              id: action?.id,
              actionType: action.type,
              [ACTIONS_CONFIG_PAYLOAD_ENUM[action.type]]: {
                ...action[ACTIONS_ENUM.Dialog],
              },
            };
          default:
            return {};
        }
      });

    return payload as Action[];
  }, [actionOrder, actions]);

  return {
    actions,
    actionOrder,
    onAddAction: handleAddNewAction,
    onUpdateActionType: handleUpdateActionType,
    onDeleteAction: handleRemoveAction,
    onCompleteSteps: handleCompleteSteps,
    onUpdateAction: handleUpdateAction,
    onReorderActions: handleReorderActions,
    onCreatePayloadForBE: createPayloadForBE,
    isActionsSetup,
  };
};
